/**
 * Canonical content policy — the ONE gate every write surface (pipeline,
 * editor, MCP agent) passes before content persists. Parse-based, never
 * regex-over-HTML. Renderers still escape defensively downstream; this
 * layer exists so stored content is already clean.
 *
 * Scope here: rich text, URLs, section CSS, typed head contributions.
 * (JSON-in-script escaping lives in the renderer's jsonLd().)
 */
import sanitizeHtml from 'sanitize-html';

/** Tags a customer richtext field may contain. Structural/layout tags stay out. */
const RICHTEXT_TAGS = ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 's', 'a', 'ul', 'ol', 'li', 'blockquote'];

const RICHTEXT_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: RICHTEXT_TAGS,
  allowedAttributes: { a: ['href', 'rel'] },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  // Root-relative and fragment links are fine; protocol-relative is not.
  allowProtocolRelative: false,
  disallowedTagsMode: 'discard',
  transformTags: {
    a: sanitizeHtml.simpleTransform('a', { rel: 'noopener' }),
  },
};

/** Sanitize a richtext value to the allowlist. Always returns safe HTML. */
export function sanitizeRichText(html: string): string {
  return sanitizeHtml(html, RICHTEXT_OPTIONS);
}

/** True when sanitizing is a no-op (input already conforms to the policy). */
export function isCleanRichText(html: string): boolean {
  return sanitizeRichText(html) === html;
}

const SAFE_URL_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * The ONE canonical URL check for content values (href/src). Accepts absolute
 * http(s)/mailto/tel, root-relative paths, and in-page fragments. Rejects
 * everything else: javascript:/data:/file:/vbscript:, protocol-relative
 * (`//host` and the backslash variant `/\host` browsers normalize to it),
 * relative-without-root, and scheme smuggling via tab/newline/case (the URL
 * parser strips those into the scheme, which then fails the allowlist).
 *
 * render-time `safeUrl` (escape.ts) delegates here so there is exactly one
 * policy, not two that drift.
 */
/** Tab/newline/other control chars are stripped by browser URL parsing, so
 *  `/\t/evil` becomes `//evil` — reject any URL carrying them. */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/** True for a genuinely same-origin root-relative path (`/a/b`, not `//x`,
 *  `/\x`, or anything a browser would re-home off-origin). */
function isSameOriginPath(value: string): boolean {
  return value.startsWith('/') && !value.startsWith('//') && value[1] !== '\\' && !hasControlChars(value);
}

export function isAllowedUrl(raw: string): boolean {
  const value = raw.trim();
  if (value === '' || hasControlChars(value)) return false;
  if (value.startsWith('#') || isSameOriginPath(value)) return true;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false; // relative-without-root, or unparseable — reject at policy level
  }
  return SAFE_URL_SCHEMES.has(parsed.protocol);
}

/**
 * Same-origin form-action check: a single-slash root-relative path only.
 * Rejects `//host` (cross-origin), backslash/control-char variants, and
 * absolute URLs.
 */
export function isAllowedFormAction(raw: string): boolean {
  return isSameOriginPath(raw.trim());
}

/** Hosts whose iframes are allowed (maps/video embeds). https only. */
const EMBED_HOSTS = new Set([
  'www.google.com', 'google.com', 'maps.google.com',
  'www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com',
  'player.vimeo.com', 'www.openstreetmap.org',
]);

/**
 * The ONE embed-iframe policy: https + allowlisted host only (google only for
 * its /maps path). Used by both freeform sanitization and pattern renderers
 * so no iframe-emitting surface can drift onto a generic URL check.
 */
export function isAllowedEmbedSrc(raw: string): boolean {
  const value = raw.trim();
  if (hasControlChars(value)) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || !EMBED_HOSTS.has(url.hostname)) return false;
  if (url.hostname.endsWith('google.com')) return url.pathname.startsWith('/maps');
  return true;
}

export interface CssIssue {
  code:
    | 'import-forbidden'
    | 'external-url-forbidden'
    | 'expression-forbidden'
    | 'behavior-forbidden'
    | 'html-breakout-forbidden';
  detail: string;
}

/**
 * Decode CSS escapes so `@im\70ort` / `e\78pression(` can't slip past the
 * substring checks. Handles `\XX...` hex (1-6 digits, optional trailing
 * whitespace) and `\c` single-char escapes.
 */
export function decodeCssEscapes(css: string): string {
  return css.replace(/\\([0-9a-fA-F]{1,6})\s?|\\(.)/g, (_match, hex: string | undefined, char: string | undefined) => {
    if (hex !== undefined) {
      const code = Number.parseInt(hex, 16);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
    }
    return char ?? '';
  });
}

/**
 * Section CSS restrictions. The CSS is emitted RAW into `<style>…</style>`,
 * so on top of the resource/script rules it must not be able to break out of
 * that element. Comments and CSS escapes are neutralized before checks.
 */
export function checkSectionCss(css: string): CssIssue[] {
  const issues: CssIssue[] = [];
  // Check the HTML-breakout FIRST on the raw source: HTML raw-text parsing of
  // <style> does not honor CSS comments, so `/* </style><script> */` would
  // break out even though comment-stripping would hide it from later checks.
  if (css.includes('<')) {
    issues.push({ code: 'html-breakout-forbidden', detail: 'section css must not contain "<" (would break out of <style>)' });
  }
  const stripped = decodeCssEscapes(css.replace(/\/\*[\s\S]*?\*\//g, ''));
  const compact = stripped.replace(/\s+/g, '').toLowerCase();
  if (compact.includes('@import')) {
    issues.push({ code: 'import-forbidden', detail: '@import is not allowed in section css' });
  }
  if (compact.includes('expression(')) {
    issues.push({ code: 'expression-forbidden', detail: 'expression() is not allowed' });
  }
  if (compact.includes('behavior:') || compact.includes('-moz-binding')) {
    issues.push({ code: 'behavior-forbidden', detail: 'behavior:/-moz-binding is not allowed' });
  }
  // Match url() with optional quotes; the charset allows `)` inside quotes.
  for (const match of stripped.matchAll(/url\(\s*(?:"([^"]*)"|'([^']*)'|([^)]*))\s*\)/gi)) {
    const target = (match[1] ?? match[2] ?? match[3] ?? '').trim();
    const isFragment = target.startsWith('#');
    if (target !== '' && !isSameOriginPath(target) && !isFragment) {
      issues.push({ code: 'external-url-forbidden', detail: `url(${target.slice(0, 60)}) must be root-relative` });
    }
  }
  return issues;
}

/** Typed head contributions — replaces arbitrary extraHead strings. */
export interface HeadContribution {
  /** Stylesheet URLs (e.g. font CSS). https or root-relative. */
  stylesheets?: string[] | undefined;
  /** Preconnect origins. https only. */
  preconnect?: string[] | undefined;
}

export interface HeadIssue {
  code: 'stylesheet-url-rejected' | 'preconnect-origin-rejected';
  detail: string;
}

export function checkHeadContribution(head: HeadContribution): HeadIssue[] {
  const issues: HeadIssue[] = [];
  for (const url of head.stylesheets ?? []) {
    const ok = isAllowedUrl(url) && (url.startsWith('https://') || (url.startsWith('/') && !url.startsWith('//')));
    if (!ok) issues.push({ code: 'stylesheet-url-rejected', detail: url.slice(0, 100) });
  }
  for (const origin of head.preconnect ?? []) {
    if (!origin.startsWith('https://')) {
      issues.push({ code: 'preconnect-origin-rejected', detail: origin.slice(0, 100) });
    }
  }
  return issues;
}
